import React, { useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Uploader, Icon, Alert, Loader } from 'rsuite';
import Stack from '@mui/material/Stack';

import CONFIG from '../../config';
import {
  getCampaignLandingOrganizationId,
  updateCampaignElementValue
} from '../../redux/modules/campaignLandingPage';
import { convertAcceptedFileTypes, getIsValidFileType, getFileTypeLabels } from '../../utils';
import { useFileTypesData } from '../../hooks';
import styles from './FileUploadInput.module.less';
import { VpHoverInfo } from '../../vp-ui';

const MAX_FILE_SIZE = 4500000;
const iconProps = {
  size: '16px'
};

const FileUploadInput = ({ _updateCampaignElementValue, element, qid, organizationId }) => {
  const { isLoading, data } = useFileTypesData();
  const allowedTypesLabel = getFileTypeLabels(element?.allowedValues, data?.data);

  const postData = {
    organizationId
  };
  const [hasFile, setHasFile] = useState(false);
  const accept = convertAcceptedFileTypes(element?.allowedValues);

  const onSuccess = (res) => {
    const attachment = {
      id: res?.attachmentId,
      filename: res?.filename,
      extension: res?.extension,
      path: res?.path,
      size: res?.size,
      originalFilename: res?.originalFileName,
      contentType: res?.contentType
    };
    _updateCampaignElementValue({ qid, attachment, isUploading: false });
  };

  const onRemove = () => {
    _updateCampaignElementValue({ qid, attachment: null });
    setHasFile(false);
  };

  const onError = (reason) => {
    const failReason = reason?.response ? ` ${reason?.response}.` : '';
    Alert.error(`Error uploading file.${failReason}`, 7500);
    _updateCampaignElementValue({ qid, isUploading: false });
  };

  const onUpload = () => {
    setHasFile(true);
    _updateCampaignElementValue({ qid, isUploading: true });
  };

  const isValidFileType = (file) => {
    const isValid = getIsValidFileType(file?.[0]?.blobFile?.name, element?.allowedValues);

    if (isValid) {
      return true;
    }

    Alert.error('Invalid file type', 7500);
    return false;
  };

  const isValidFileSize = (file) => {
    const isValid = file?.[0]?.blobFile?.size < MAX_FILE_SIZE;

    if (isValid) {
      return true;
    }

    Alert.error('File exceeds maximum size of 4MB', 7500);
    return false;
  };

  const shouldQueueUpdate = (file) => {
    return isValidFileType(file) && isValidFileSize(file);
  };
  const acceptedFiles = `Accepted File Types: ${
    isLoading ? <Loader className={styles.vpFileTypesLoader} size="xs" /> : `${allowedTypesLabel}`
  }`;

  return (
    <section className={styles.vpFileUploadInputContainer}>
      <div className={styles.vpFileUploadInputLabelContainer}>
        <Stack direction="row" alignItems="center" gap="5px">
          <strong>
            {element?.label}
            {element?.required && <span className={styles.vpRequiredLabel}>*</span>}
          </strong>
          <VpHoverInfo label={acceptedFiles} icon="Info" iconProps={iconProps} />
        </Stack>
      </div>
      <Uploader
        multiple={false}
        accept={accept}
        draggable
        data={postData}
        action={CONFIG?.AWS?.fileUploadUrl}
        onError={(reason) => onError(reason)}
        onUpload={() => onUpload()}
        onSuccess={(res, file) => onSuccess(res, file)}
        onRemove={() => onRemove()}
        disabled={hasFile}
        shouldQueueUpdate={shouldQueueUpdate}
      >
        <div>
          <div className={styles.vpFileUploadArea}>
            {hasFile ? (
              <span>
                <Icon icon="attachment" size="lg" />
                File selected. Delete selected file to upload a different one.
              </span>
            ) : (
              <span>
                <Icon icon="attachment" size="lg" />
                Click or drag a file to this area to upload. (Max 4MB)
              </span>
            )}
          </div>
        </div>
      </Uploader>
    </section>
  );
};

FileUploadInput.propTypes = {
  _updateCampaignElementValue: PropTypes.func,
  element: PropTypes.object,
  qid: PropTypes.string,
  organizationId: PropTypes.string
};

const mapStateToProps = (state) => ({
  organizationId: getCampaignLandingOrganizationId(state)
});

const mapDispatchToProps = {
  _updateCampaignElementValue: updateCampaignElementValue
};

export default connect(mapStateToProps, mapDispatchToProps)(FileUploadInput);



// WEBPACK FOOTER //
// ./src/components/CampaignLanding/FileUploadInput.jsx